import type Collection from '@netx/core/lib/netx-collection';
import _ from 'underscore';

import {useEffect, useState} from 'react';
import useComponentIsMounted from './useComponentIsMounted';
import useConstCallback from './useConstCallback';

type ParseFn<TCollection extends Collection = Collection> = (
	collection: TCollection,
) => TCollection['models'][number]['attributes'][];

const parseDefault = <TCollection extends Collection = Collection>(
	collection: TCollection,
) => [...collection.models.map((model) => ({...model.attributes}))];

/**
 * Handles piping a backbone collection into a react state
 * @deprecated
 */
export function useBackboneCollection<
	TCollection extends Collection = Collection,
>(
	collection: TCollection,
	fetchOnMount = true,
	parse: ParseFn<TCollection> = parseDefault,
) {
	const {act} = useComponentIsMounted();

	const [attributes, setAttributes] = useState<
		TCollection['models'][number]['attributes'][]
	>(parse(collection));

	// Pull the attributes or array of attributes from model or collection
	// Set them on state
	const pullAndSetData = useConstCallback(() => {
		act(() => {
			setAttributes(parse(collection));
		});
	});

	// Subscribe to the collection on first render only
	useEffect(() => {
		pullAndSetData();

		const debouncedPullAndSetData = _.debounce(pullAndSetData, 20);
		collection.on('add change remove reset sort sync', debouncedPullAndSetData);

		const preferredFetchMethod = collection.preferredFetchMethod || 'fetch';
		// @ts-ignore
		fetchOnMount !== false && collection[preferredFetchMethod]();

		// Unsubscribe when component unmounted
		return () => {
			collection.off(undefined, debouncedPullAndSetData);
		};
	}, []);

	return attributes;
}

export default useBackboneCollection;
